// Canonical production origin for absolute URLs in structured data — matches
// the Rails Organization schema. Staging is noindex, so prod URLs are correct.
export const CANONICAL_ORIGIN = 'https://www.riparide.com'

export const absoluteUrl = (
  path?: string | null,
  origin: string = CANONICAL_ORIGIN,
): string | undefined => {
  if (!path) return undefined
  return /^https?:\/\//.test(path) ? path : `${origin}${path}`
}

export interface ImageSet {
  large?: string | null
  medium?: string | null
}

export const cardImage = (image?: ImageSet | null): string | undefined =>
  image?.large || image?.medium || undefined

export interface RatingInput {
  reviewScore?: number | string | null
  reviewCount?: number | null
}

// review_score is a 0–1 ratio stored to 2 decimals; the site shows it as a whole
// 0–100 "% Recommended".
export const aggregateRating = ({ reviewScore, reviewCount }: RatingInput) => {
  const count = Number(reviewCount)
  if (!count || count <= 0) return undefined

  return {
    '@type': 'AggregateRating',
    ratingValue: Math.round(Number(reviewScore) * 100),
    reviewCount: count,
    bestRating: 100,
    worstRating: 0,
  }
}

const CURRENCY_SYMBOLS: Record<string, string> = {
  AUD: '$',
  NZD: '$',
  USD: '$',
}

export const priceRange = (
  price?: number | string | null,
  weekendPrice?: number | string | null,
  currency?: string | null,
): string | undefined => {
  const symbol = CURRENCY_SYMBOLS[(currency || '').toUpperCase()] || '$'
  const values = [price, weekendPrice]
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value) && value > 0)
  const unique = Array.from(new Set(values)).sort((a, b) => a - b)

  if (!unique.length) return undefined

  return unique.map((value) => `${symbol}${Math.round(value)}`).join(' – ')
}

export interface CardListing extends RatingInput {
  url?: string | null
  title?: string | null
  promoImage?: ImageSet | null
  price?: number | string | null
  weekendPrice?: number | string | null
  currency?: string | null
  listingType?: { slug?: string | null } | null
}

// Grid cards carry listingType and must be gated to accommodation; homepage
// featured listings omit it — those are always stays, so treat as lodging.
const isLodging = (listing: CardListing): boolean => {
  const slug = listing.listingType?.slug
  return slug ? slug === 'accommodation' : true
}

// A reference-or-full LodgingBusiness for a stay (no offers — those live on the
// canonical listing page). @id is the canonical URL, linking the two.
export const lodgingBusiness = (
  listing: CardListing | null | undefined,
  origin: string = CANONICAL_ORIGIN,
) => {
  if (!listing || !isLodging(listing)) return undefined

  const url = absoluteUrl(listing.url, origin)
  const range = priceRange(
    listing.price,
    listing.weekendPrice,
    listing.currency,
  )

  return {
    '@type': 'LodgingBusiness',
    '@id': url,
    name: listing.title || undefined,
    url,
    image: cardImage(listing.promoImage),
    priceRange: range,
    aggregateRating: aggregateRating(listing),
  }
}

export interface AttractionInput {
  url?: string | null
  name?: string | null
  image?: string | null
  description?: string | null
  location?: { lat?: number | null; lng?: number | null } | null
}

// An adventure (a thing to do) as a schema.org TouristAttraction.
export const touristAttraction = (
  attraction: AttractionInput | null | undefined,
  origin: string = CANONICAL_ORIGIN,
) => {
  const url = absoluteUrl(attraction?.url, origin)
  if (!url) return undefined

  const lat = attraction?.location?.lat
  const lng = attraction?.location?.lng
  const geo =
    lat != null && lng != null
      ? { '@type': 'GeoCoordinates', latitude: lat, longitude: lng }
      : undefined

  return {
    '@type': 'TouristAttraction',
    '@id': url,
    name: attraction?.name || undefined,
    url,
    image: attraction?.image || undefined,
    description: attraction?.description || undefined,
    geo,
  }
}
